import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../../store/store';
import { clearAuthData } from '../../store/slices/authSlice';
import Loader from '../Loader';
import type { appConfigs } from '../types/types';

const LoginRedirect: React.FC = () => {
    const { appConfigs: configs, token } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        // If the user already has a session, take them to the dashboard
        const storedToken = localStorage.getItem('jwtToken');
        if ((token != null && token !== '') || (storedToken != null && storedToken !== '')) {
            navigate('/dashboard', { replace: true });
            return;
        }

        // Otherwise, clear any partial session data and redirect to Engage logout
        localStorage.clear();
        sessionStorage.clear();
        dispatch(clearAuthData());

        const findEngageUrl = (conf: appConfigs | appConfigs[] | null): string | null => {
            if (conf === null) return null;
            return Array.isArray(conf)
                ? (conf as appConfigs[]).find(
                    (config) => config.configName === 'BaseURLFE' && config.configType === 'EngageApp'
                )?.configValue ?? null
                : null;
        };

        let redirectUrl = findEngageUrl(configs);

        // Fallback to localStorage if Redux state is not yet available
        if (redirectUrl === null || redirectUrl === '') {
            const storedConfigsString = localStorage.getItem('appConfigs');
            if (storedConfigsString != null && storedConfigsString !== '') {
                try {
                    const parsedConfigs = JSON.parse(storedConfigsString) as appConfigs | appConfigs[];
                    redirectUrl = findEngageUrl(parsedConfigs);
                } catch (e) {
                    console.error('Error parsing stored appConfigs:', e);
                }
            }
        }

        // Use environment variable as a final fallback to prevent a hanging Loader
        if (redirectUrl === null || redirectUrl === '') {
            redirectUrl = process.env.REACT_APP_ENGAGE_URL ?? null;
        }

        // Perform the external redirection to Engage logout
        if (redirectUrl != null && redirectUrl !== '') {
            window.location.href = redirectUrl.endsWith('/') ? redirectUrl + 'logout' : redirectUrl + '/logout';
        }
    }, [configs, token, navigate, dispatch]);

    return (
        <div className="flex items-center justify-center h-screen">
            <Loader />
        </div>
    );
};

export default LoginRedirect;
